/**
 * Owner: Masa Bayu
 * File: founder-platform-accounts.ts
 * Date:Time: 2026-09-27 10:49
 * Description: Three named accounts with email and password login.
 */
import { createQxreadAccount, createQxreadSession } from './identity';
import { QX_PLATFORM_CAPABILITIES, } from './platform-access-principals';
const CREATED_AT = '2026-09-27T02:49:00.000Z';
const PBKDF2_ITERATIONS = 210_000;
const PBKDF2_BITS = 256;
export const QX_FOUNDER_PLATFORM_EMAIL = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
const LOGIN_DENIED = Object.freeze({
    ok: false,
    reason: 'login denied',
});
function member(seat, displayName, email, accountId) {
    const normalized = email.trim().toLowerCase();
    if (!QX_FOUNDER_PLATFORM_EMAIL.test(normalized)) {
        throw new Error(`Founder platform email is not usable: ${email}`);
    }
    const account = createQxreadAccount({
        accountId,
        displayName,
        roles: ['author'],
        createdAt: CREATED_AT,
    });
    return Object.freeze({
        seat,
        email: normalized,
        account,
        assignment: Object.freeze({
            accountId: account.accountId,
            capabilities: QX_PLATFORM_CAPABILITIES,
            authorityBypass: false,
        }),
    });
}
export const QX_FOUNDER_PLATFORM_MEMBERS = Object.freeze([
    member('founder', 'Masa Bayu', 'hazamre@gmail.com', 'qxrdacc_masa_bayu'),
    member('assistant-1', 'Izwahanie Rosli', 'izwahaneez@gmail.com', 'qxrdacc_izwahanie'),
    member('assistant-2', 'Suhaila Ghazali', 'suhailaghazali05@gmail.com', 'qxrdacc_suhaila'),
]);
const PASSWORD_CREDENTIALS = Object.freeze({
    qxrdacc_masa_bayu: Object.freeze({
        saltHex: '9ba1e1bdf59f7e04190b1321b7e204df',
        hashHex: '53dc23b58dc938d4e4814062b3e8fb1b98aaf46dbf221029927e4a942ce4ec48',
    }),
    qxrdacc_izwahanie: Object.freeze({
        saltHex: '209067c73a7fee7015b69967b7f9d716',
        hashHex: '808eee671f125c15c9c1fd9c2389e182285a2eb55f46329e1c28e718900ea96a',
    }),
    qxrdacc_suhaila: Object.freeze({
        saltHex: '8102dec82fb93c011c91b94d66d77b80',
        hashHex: 'c8a54ee56a716f22c9287d95eede0b311768187348f611cbe260bdc1c54f2d1c',
    }),
});
const UNKNOWN_EMAIL_CREDENTIAL = Object.freeze({
    saltHex: '4fc555f1675da730bee545f1ad1ff6d7',
    hashHex: 'a9e8014d022ed6a0181a4378636a915f14703ead6bf7205bc0c875c4cf6878f0',
});
export function founderPlatformAssignments() {
    return Object.freeze(QX_FOUNDER_PLATFORM_MEMBERS.map((row) => row.assignment));
}
export function findFounderPlatformMemberByEmail(email) {
    const normalized = email.trim().toLowerCase();
    const match = QX_FOUNDER_PLATFORM_MEMBERS.find((row) => row.email === normalized);
    return match ?? null;
}
function saltBytes(hex) {
    const nodeBuffer = globalThis.Buffer;
    if (nodeBuffer !== undefined) {
        return nodeBuffer.from(hex, 'hex');
    }
    const bytes = new Uint8Array(hex.length / 2);
    for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Number.parseInt(hex.slice(index * 2, index * 2 + 2), 16);
    }
    return bytes;
}
function bytesToHex(bytes) {
    return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
function hashesMatch(actual, expected) {
    if (actual.length !== expected.length)
        return false;
    let diff = 0;
    for (let index = 0; index < actual.length; index += 1) {
        diff |= actual.charCodeAt(index) ^ expected.charCodeAt(index);
    }
    return diff === 0;
}
async function derivePasswordHash(password, saltHex) {
    const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits']);
    const bits = await crypto.subtle.deriveBits({
        name: 'PBKDF2',
        hash: 'SHA-256',
        salt: saltBytes(saltHex),
        iterations: PBKDF2_ITERATIONS,
    }, key, PBKDF2_BITS);
    return bytesToHex(new Uint8Array(bits));
}
export async function loginFounderPlatformAccount(email, password) {
    const memberRow = findFounderPlatformMemberByEmail(email);
    const credential = memberRow === null
        ? UNKNOWN_EMAIL_CREDENTIAL
        : PASSWORD_CREDENTIALS[memberRow.account.accountId];
    if (credential === undefined ||
        typeof password !== 'string' ||
        password.length < 12 ||
        password.length > 128) {
        return LOGIN_DENIED;
    }
    const actual = await derivePasswordHash(password, credential.saltHex);
    if (memberRow === null || !hashesMatch(actual, credential.hashHex)) {
        return LOGIN_DENIED;
    }
    return Object.freeze({
        ok: true,
        member: memberRow,
        session: createQxreadSession({
            accountId: memberRow.account.accountId,
            state: 'authenticated',
            issuedAt: new Date().toISOString(),
        }),
    });
}
//# sourceMappingURL=founder-platform-accounts.js.map